/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { NetworkStatus, gql, useQuery } from '@apollo/client';
import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconFilter from '@apollo/icons/default/IconFilter.svg';
import IconSortAscending from '@apollo/icons/small/IconSortAscending.svg';
import IconSortDescending from '@apollo/icons/small/IconSortDescending.svg';
import {
  Button,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuContent,
  MenuHeader,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  MenuTitle,
  Tooltip,
} from '@apollo/orbit';
import { jsx } from '@emotion/react';
import { xor } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  Redirect,
  useHistory,
  useLocation,
  useRouteMatch,
} from 'react-router-dom';

import { useIsAppInBackgroundContext } from 'src/app/appLayout/hooks/useIsAppInBackground';
import { lastReportedQuery } from 'src/app/graph/operationsPage/insightsL3Nav/insightsList/lastReportedQuery';
import {
  coordinateNotFoundRouteConfig,
  fieldRouteConfig,
  insightsRouteConfig,
} from 'src/app/graph/routes';
import { ClientFilterPopover } from 'src/components/clientFilter/ClientFilterPopover';
import { filterTypes } from 'src/components/common/filterProvider/FilterProvider';
import { MultiSelect } from 'src/components/multiSelect/MultiSelect';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useFieldInsightsFilter } from 'src/hooks/useInsightsFilter';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { TimeRangeOption } from 'src/lib/config/timeRangeOptions';
import { CustomTimeRange, date } from 'src/lib/date/date';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import {
  FieldInsightsListOrderByColumn,
  Ordering,
} from 'src/lib/graphqlTypes/types';
import {
  NewNormalizedMetricsFilterField,
  NormalizedFieldsMetricsField,
  NormalizedFieldsMetricsFilterField,
  isValidFieldsMetricsField,
  isValidFieldsMetricsFilterField,
  metricConfig,
} from 'src/lib/metrics';
import { catchAllRouteConfig } from 'src/lib/routeConfig/catchAllRoute';
import { mergeQueryParams } from 'src/lib/routing';

import { FieldsListBody } from './insightsListBody/FieldsListBody';
import { FieldsListItemPlaceholder } from './insightsListItemPlaceholder/FieldsListItemPlaceholder';
import { InsightsNoSearchResults } from './insightsNoSearchResults/InsightsNoSearchResults';
import { SelectedFieldsListItem } from './insightsSelectedListItem/SelectedFieldsListItem';
import { PaginationControls } from './paginationControls/PaginationControls';
import { SearchFilter } from './searchFilter/SearchFilter';
import { TotalCountBadge } from './totalCountBadge/TotalCountBadge';

export const fieldsListQuery = gql`
  query FieldsListQuery(
    $graphId: ID!
    $name: String!
    $first: Int
    $after: String
    $last: Int
    $before: String
    $timeFrom: Timestamp!
    $timeTo: Timestamp!
    $filter: FieldInsightsListFilterInput
    $orderBy: FieldInsightsListOrderByInput
  ) {
    graph(id: $graphId) {
      id
      variant(name: $name) {
        id
        fieldInsightsList(
          first: $first
          after: $after
          last: $last
          before: $before
          from: $timeFrom
          to: $timeTo
          filter: $filter
          orderBy: $orderBy
        ) {
          fields: nodes {
            parentType
            fieldName
            referencingOperationCount
            referencingOperationCountPerMin
            executionCount
            estimatedExecutionCount
            errorCount
            errorCountPerMin
            errorPercentage
            p50LatencyMs
            p90LatencyMs
            p95LatencyMs
            p99LatencyMs
          }
          pageInfo {
            startCursor
            endCursor
          }
          totalCount
        }
      }
    }
  }
`;

const orderByColumnMap: {
  [key in NormalizedFieldsMetricsField]: FieldInsightsListOrderByColumn;
} = {
  alphabetically: FieldInsightsListOrderByColumn.PARENT_TYPE_AND_FIELD_NAME,
  requestsCount: FieldInsightsListOrderByColumn.REFERENCING_OPERATION_COUNT,
  requestRate:
    FieldInsightsListOrderByColumn.REFERENCING_OPERATION_COUNT_PER_MIN,
  executions: FieldInsightsListOrderByColumn.ESTIMATED_EXECUTION_COUNT,
  errorCount: FieldInsightsListOrderByColumn.ERROR_COUNT,
  errorPercentage: FieldInsightsListOrderByColumn.ERROR_PERCENTAGE,
  errorCountPerMin: FieldInsightsListOrderByColumn.ERROR_COUNT_PER_MIN,
  p50LatencyMs: FieldInsightsListOrderByColumn.SERVICE_TIME_P50,
  p90LatencyMs: FieldInsightsListOrderByColumn.SERVICE_TIME_P90,
  p95LatencyMs: FieldInsightsListOrderByColumn.SERVICE_TIME_P95,
  p99LatencyMs: FieldInsightsListOrderByColumn.SERVICE_TIME_P99,
};

const getFieldSortColumn = (
  fieldsSort: NormalizedFieldsMetricsField | undefined,
): FieldInsightsListOrderByColumn => {
  return fieldsSort
    ? orderByColumnMap[fieldsSort]
    : FieldInsightsListOrderByColumn.REFERENCING_OPERATION_COUNT;
};

interface FieldsListProps {
  range: TimeRangeOption;
  bounds: CustomTimeRange;
  filter: filterTypes;
  graphId: string;
  graphVariant: string;
  clientFilterCount?: number;
}

interface GroupedValue {
  values:
    | NormalizedFieldsMetricsFilterField[]
    | NewNormalizedMetricsFilterField[];
  opposing: boolean;
  dividerAfter?:
    | NormalizedFieldsMetricsFilterField
    | NewNormalizedMetricsFilterField;
}
export type GroupedValues = Array<GroupedValue>;

const filterOrderMap = new Map<NormalizedFieldsMetricsFilterField, number>([
  ['used', 0],
  ['unused', 1],
  ['deprecated', 2],
  ['nonDeprecated', 3],
]);

const groupedValues: GroupedValues = [
  {
    values: ['used', 'unused'],
    opposing: true,
    dividerAfter: 'unused',
  },
  {
    values: ['deprecated', 'nonDeprecated'],
    opposing: true,
  },
];
export const FieldsList = (props: FieldsListProps) => {
  const { range, bounds, filter, graphId, graphVariant, clientFilterCount } =
    props;
  const { routeParams: filterParams, setFilterParams } =
    useFieldInsightsFilter();
  const {
    fieldsDir,
    fieldSearch,
    fieldsAfter,
    fieldsBefore,
    fieldsPage = 0,
    fieldsSort = 'requestsCount',
    fieldsFilter: fieldsFilterTemp = [],
    fieldsPageDir = 'first',
    fastModeRange,
  } = filterParams;
  const { typeName, fieldName } = useRouteParams(
    fieldRouteConfig,
    catchAllRouteConfig,
  );

  const sortDirectionIsDescending =
    fieldsDir === 'desc' || fieldsDir === undefined;
  const location = useLocation();
  const history = useHistory();

  const isInBackground = useIsAppInBackgroundContext();
  const [inViewRef, inView] = useInView();
  const trackCustomEvent = useTrackCustomEvent();
  const { timeFrom, timeTo, pollInterval } = date.getDateBounds(
    range,
    bounds,
    undefined,
    fastModeRange,
  );

  const { data: lastReportedData } = useQuery(lastReportedQuery, {
    notifyOnNetworkStatusChange: true,
    variables: {
      graphId: graphId || '',
      graphVariant: graphVariant || '',
    },
    skipPollAttempt: useCallback(() => isInBackground, [isInBackground]),
  });

  const [fieldsSearchInputValue, setFieldsSearchInputValue] = useState(
    fieldSearch ?? '',
  );

  useEffect(() => {
    // We manage the non-debounced search state in this component but the URL
    // owns the debounced search state. If components reset the URL state we
    // need to sync those changes here.
    if (!fieldSearch) {
      setFieldsSearchInputValue('');
    }
  }, [fieldSearch]);

  let fieldsFilter = fieldsFilterTemp;
  const reportingEnabled = !!lastReportedData?.service?.lastReportedAt;

  if (fieldsFilter.length === 0 && reportingEnabled) {
    fieldsFilter = ['used'];
  }

  const resetFieldSearchInputValue = () => {
    setFieldsSearchInputValue('');
  };

  const selectedFieldCoordinate =
    typeName && fieldName ? `${typeName}.${fieldName}` : undefined;

  const statsSortKey = isValidFieldsMetricsField(fieldsSort)
    ? fieldsSort
    : 'requestsCount';
  const statsFilterKeys =
    fieldsFilter !== 'none' && isValidFieldsMetricsFilterField(fieldsFilter)
      ? fieldsFilter
      : [];

  const isOnCoordinateNotFoundRoute = useRouteMatch(
    coordinateNotFoundRouteConfig.definition,
  );
  const currentRouteConfig = isOnCoordinateNotFoundRoute
    ? coordinateNotFoundRouteConfig
    : fieldRouteConfig;

  const updateFilterOption = (
    selectedFilterOptions: NormalizedFieldsMetricsFilterField[],
  ) => {
    setFilterParams(currentRouteConfig, {
      fieldsFilter:
        // When clearing filters, represent this on the url via 'none'
        selectedFilterOptions.length > 0 ? selectedFilterOptions : 'none',
      fieldsPage: 0,
      fieldsAfter: undefined,
      fieldsBefore: undefined,
    });

    // Track when option is added to filter
    const wasOptionAdded = selectedFilterOptions.length > fieldsFilter.length;
    const [optionAdded] = xor(selectedFilterOptions, fieldsFilter);
    if (wasOptionAdded && optionAdded) {
      trackCustomEvent({
        category: 'Insights L3 Fields List',
        action: 'selected_fields_filter',
        label: optionAdded,
      });
    }
  };

  const updateSortOption = (newFieldsSort: NormalizedFieldsMetricsField) => {
    const asc = sortDirectionLongformToReadableMap[Ordering.ASCENDING];
    const desc = sortDirectionLongformToReadableMap[Ordering.DESCENDING];
    const prevFieldsSort = statsSortKey;
    // When sorting Alphabetically, sort asc by default. Sort desc for any other
    // sort metric
    const newFieldsDir =
      newFieldsSort === 'alphabetically'
        ? asc
        : prevFieldsSort === 'alphabetically'
          ? desc
          : fieldsDir;

    setFilterParams(currentRouteConfig, {
      fieldsSort: newFieldsSort,
      fieldsDir: newFieldsDir,
      fieldsPage: 0,
      fieldsAfter: undefined,
      fieldsBefore: undefined,
    });

    trackCustomEvent({
      category: 'Insights L3 Fields List',
      action: 'selected_fields_sort',
      label: newFieldsSort,
    });
  };

  const sortDirectionLongformToReadableMap = {
    ASCENDING: 'asc' as const,
    DESCENDING: 'desc' as const,
  };

  const updateSortDirection = (direction: Ordering) => {
    setFilterParams(currentRouteConfig, {
      fieldsDir: sortDirectionLongformToReadableMap[direction],
      fieldsPage: 0,
      fieldsAfter: undefined,
      fieldsBefore: undefined,
    });

    trackCustomEvent({
      category: 'Insights L3 Fields List',
      action: 'selected_fields_sort_direction',
      label: direction,
    });
  };

  const fieldListPageSize = 50;
  const paginationParams = fieldsAfter
    ? {
        first: fieldListPageSize,
        after: fieldsAfter,
      }
    : fieldsBefore
      ? {
          last: fieldListPageSize,
          before: fieldsBefore,
        }
      : fieldsPageDir === 'last'
        ? {
            last: fieldListPageSize,
          }
        : {
            first: fieldListPageSize,
          };

  let isDeprecated = null;
  if (
    statsFilterKeys.includes('deprecated') &&
    !statsFilterKeys.includes('nonDeprecated')
  ) {
    isDeprecated = true;
  }
  if (
    !statsFilterKeys.includes('deprecated') &&
    statsFilterKeys.includes('nonDeprecated')
  ) {
    isDeprecated = false;
  }

  let isUnused = null;
  if (statsFilterKeys.includes('unused') && !statsFilterKeys.includes('used')) {
    isUnused = true;
  }
  if (!statsFilterKeys.includes('unused') && statsFilterKeys.includes('used')) {
    isUnused = false;
  }
  const userSelectableFilterKeys = Object.keys(
    metricConfig.allFieldsFilters,
  ) as NormalizedFieldsMetricsFilterField[];

  const skip = isInBackground || !inView;

  const {
    data: newData,
    previousData,
    networkStatus,
    stopPolling,
  } = useQuery<
    GraphQLTypes.FieldsListQuery,
    GraphQLTypes.FieldsListQueryVariables
  >(fieldsListQuery, {
    variables: {
      ...paginationParams,
      graphId: graphId || '',
      name: graphVariant || '',
      timeFrom,
      timeTo,
      filter: {
        ...filter,
        search: fieldSearch,
        isDeprecated,
        isUnused,
      },
      orderBy: {
        column: getFieldSortColumn(statsSortKey),
        direction:
          fieldsDir === 'asc' ? Ordering.ASCENDING : Ordering.DESCENDING,
      },
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    pollInterval,
    skip,
    skipPollAttempt: useCallback(() => skip, [skip]),
  });

  const data = newData ?? previousData;
  const totalCount = data?.graph?.variant?.fieldInsightsList?.totalCount;

  // We see a "setVariables" state when paginating, searching, or changing filters since
  // we use the URL to hold the pagination state.  Including the others for
  // completeness.
  const activeRefetching = [
    NetworkStatus.setVariables,
    NetworkStatus.refetch,
    NetworkStatus.fetchMore,
  ].includes(networkStatus);

  // We see a "loading" state when returning from the background and
  // "poll" when polling.
  const passiveRefetching = [
    NetworkStatus.loading,
    NetworkStatus.poll,
  ].includes(networkStatus);

  const loading = activeRefetching || passiveRefetching;

  const isSelectedFieldInCurrentPage =
    !!data?.graph?.variant?.fieldInsightsList?.fields?.find(
      (field) =>
        `${field.parentType}.${field.fieldName}` === selectedFieldCoordinate,
    );

  const numPages =
    (totalCount && Math.ceil(totalCount / fieldListPageSize) - 1) ?? 1;

  const [currentAccountId] = useCurrentAccountId();
  const currentPlan = useCurrentPlan({
    skip: !currentAccountId,
    accountId: currentAccountId as string,
  });

  const canFilterOnClients = !!currentPlan.currentPlan?.clients;
  const canViewClientVersions =
    currentPlan.currentPlan?.clientVersions ?? false;

  const coordinates = data?.graph?.variant?.fieldInsightsList?.fields ?? [];

  const firstField = coordinates[0];
  // Pick the first field in the list when no field selected
  if (!selectedFieldCoordinate && firstField) {
    return (
      <Redirect
        to={fieldRouteConfig.locationFrom({
          location,
          patch: {
            typeName: firstField.parentType,
            fieldName: firstField.fieldName,
          },
          fromRouteConfig: insightsRouteConfig,
        })}
      />
    );
  }

  // If no field has been selected, there are no results in the list, and we're
  // not in a loading state redirect to the not found page
  if (
    !selectedFieldCoordinate &&
    !firstField &&
    !loading &&
    data &&
    !isOnCoordinateNotFoundRoute
  ) {
    return (
      <Redirect
        to={coordinateNotFoundRouteConfig.locationFrom({
          location,
          fromRouteConfig: insightsRouteConfig,
        })}
      />
    );
  }

  return (
    <div className="flex h-full flex-col" ref={inViewRef}>
      <>
        <div className="flex items-center">
          <SearchFilter
            placeholderText="Search for a field name or parent type"
            onInputChange={setFieldsSearchInputValue}
            inputValue={fieldsSearchInputValue}
            onSearch={(search: string) => {
              history.replace({
                ...location,
                search: mergeQueryParams(location.search, {
                  fieldSearch: search,
                  fieldsPage: 0,
                  fieldsAfter: undefined,
                  fieldsBefore: undefined,
                }),
              });
              trackCustomEvent({
                category: 'Insights L3 Fields List',
                action: 'executed_insights_fields_search',
              });
            }}
            onClear={() => {
              trackCustomEvent({
                category: 'Insights L3 Fields List',
                action: 'cleared_insights_fields_search',
              });
            }}
          />
          {canFilterOnClients && (
            <ClientFilterPopover
              graphId={graphId}
              canViewVersions={canViewClientVersions}
              clientFilterCount={clientFilterCount}
            />
          )}
        </div>
        <div className="mb-2 mt-3 flex items-center justify-between font-semibold text-link">
          <MultiSelect
            canSelectExclusive
            className="min-w-64 !p-2"
            valueTitle="Filter Fields"
            valueTitleClassName="text-sm"
            clearAllClassName="font-normal"
            clearAllText="Clear filters"
            allValues={userSelectableFilterKeys}
            groupedValues={groupedValues}
            orderValuesBy={(value) =>
              filterOrderMap.get(value) ?? assertUnreachable(value as never)
            }
            selectedValues={new Set(statsFilterKeys)}
            setSelectedValues={(selectedValues) => {
              updateFilterOption(Array.from(selectedValues.values()));
            }}
            triggerNode={
              <Button
                size="sm"
                variant="hidden"
                leftIcon={<IconFilter />}
                rightIcon={<IconChevronDown />}
              >
                {statsFilterKeys.length === 0 ||
                statsFilterKeys.length === userSelectableFilterKeys.length
                  ? 'View all'
                  : statsFilterKeys.length === 1 &&
                      statsFilterKeys[0] !== undefined
                    ? metricConfig.allFieldsFilters[statsFilterKeys[0]]
                    : `${statsFilterKeys.length} filters`}
              </Button>
            }
            labelForValue={(value) => metricConfig.allFieldsFilters[value]}
          />
          <div className="flex items-center">
            <Menu matchWidth>
              <MenuButton
                as={Button}
                size="sm"
                variant="hidden"
                rightIcon={
                  <IconChevronDown className="ml-1 text-icon-primary" />
                }
              >
                <span className="truncate">
                  {metricConfig.allFieldsMetrics[statsSortKey]}
                </span>
              </MenuButton>
              <MenuContent>
                <MenuHeader>
                  <MenuTitle>Sort fields by</MenuTitle>
                </MenuHeader>
                <MenuList>
                  <MenuOptionGroup
                    type="radio"
                    value={fieldsSort as Parameters<typeof updateSortOption>[0]}
                    onChange={(key) => {
                      updateSortOption(
                        key as Parameters<typeof updateSortOption>[0],
                      );
                    }}
                  >
                    {Object.entries(metricConfig.allFieldsMetrics).map(
                      ([key, value]) => {
                        const disableLatencyOption =
                          fieldsFilter.includes('unused') &&
                          key.includes('Latency');

                        return (
                          <MenuItemOption
                            key={key}
                            value={key}
                            isDisabled={disableLatencyOption}
                            type="radio"
                            className="group"
                            description={
                              key === 'alphabetically'
                                ? 'Total requests will be shown when selected.'
                                : undefined
                            }
                          >
                            {value}
                          </MenuItemOption>
                        );
                      },
                    )}
                  </MenuOptionGroup>
                </MenuList>
              </MenuContent>
            </Menu>
          </div>
        </div>
        {((networkStatus === NetworkStatus.loading ||
          networkStatus === NetworkStatus.setVariables) &&
          previousData?.graph?.variant?.fieldInsightsList.totalCount === 0) ||
        !data ||
        !data?.graph?.variant ||
        !inView ? (
          <FieldsListItemPlaceholder />
        ) : data?.graph?.variant?.fieldInsightsList?.totalCount === 0 ? (
          <div>
            {!!selectedFieldCoordinate && !isSelectedFieldInCurrentPage ? (
              <div className="border-t-[1px] border-primary">
                <SelectedFieldsListItem
                  fieldCoordinate={selectedFieldCoordinate}
                  loading={activeRefetching}
                />
              </div>
            ) : null}
            <InsightsNoSearchResults
              entity="Field"
              resetSearch={resetFieldSearchInputValue}
              graphId={graphId}
              graphVariant={graphVariant}
            />
          </div>
        ) : (
          <>
            <div className="hide-scrollbar flex-1 overflow-y-auto overflow-x-hidden border-t-[1px] border-primary">
              <>
                {!!selectedFieldCoordinate && !isSelectedFieldInCurrentPage && (
                  <SelectedFieldsListItem
                    fieldCoordinate={selectedFieldCoordinate}
                    loading={activeRefetching}
                  />
                )}
                <div className="ml-4 mt-4 flex items-center">
                  <Heading variant="title" className="mr-1">
                    {fieldsFilter.length > 0 ? 'Results' : 'All fields'}
                  </Heading>
                  <TotalCountBadge count={totalCount} />
                  <Tooltip
                    label={`Switch to ${
                      sortDirectionIsDescending ? 'Ascending' : 'Descending'
                    }`}
                  >
                    <IconButton
                      size="xs"
                      className="ml-auto mr-2"
                      variant="hidden"
                      aria-label={`Switch to ${
                        sortDirectionIsDescending ? 'Ascending' : 'Descending'
                      }`}
                      onClick={() => {
                        updateSortDirection(
                          sortDirectionIsDescending
                            ? Ordering.ASCENDING
                            : Ordering.DESCENDING,
                        );
                      }}
                    >
                      {sortDirectionIsDescending ? (
                        <IconSortDescending />
                      ) : (
                        <IconSortAscending />
                      )}
                    </IconButton>
                  </Tooltip>
                </div>
              </>
              <FieldsListBody
                selectedField={selectedFieldCoordinate}
                data={data}
                timeFrom={timeFrom}
                timeTo={timeTo}
                statsSortKey={statsSortKey}
                loading={activeRefetching}
              />
            </div>

            {totalCount ? (
              <PaginationControls
                currentPage={fieldsPage}
                totalCount={totalCount}
                pageSize={fieldListPageSize}
                numPages={numPages}
                loading={passiveRefetching}
                disabled={loading}
                onFirst={(nextPage) => {
                  history.replace({
                    ...location,
                    search: mergeQueryParams(location.search, {
                      fieldsAfter: undefined,
                      fieldsBefore: undefined,
                      fieldsPage: nextPage,
                      fieldsPageDir: 'first',
                    }),
                  });

                  trackCustomEvent({
                    category: 'Insights L3 Fields List',
                    action: 'selected_first_page',
                  });
                }}
                onPrevious={(nextPage) => {
                  // If we're already on the first page then get the first page
                  // of results w/out the cursor. This allows the user to get to
                  // results that may have drifted due paginating over data
                  // which is ingested in real-time.
                  if (nextPage === 0) {
                    history.replace({
                      ...location,
                      search: mergeQueryParams(location.search, {
                        fieldsAfter: undefined,
                        fieldsBefore: undefined,
                        fieldsPage: nextPage,
                        fieldsPageDir: 'first',
                      }),
                    });
                  } else {
                    history.replace({
                      ...location,
                      search: mergeQueryParams(location.search, {
                        fieldsAfter: undefined,
                        fieldsBefore:
                          data.graph?.variant?.fieldInsightsList.pageInfo
                            .startCursor ?? undefined,
                        fieldsPage: nextPage,
                        fieldsPageDir: 'last',
                      }),
                    });
                  }

                  trackCustomEvent({
                    category: 'Insights L3 Fields List',
                    action: 'selected_prev_page',
                  });
                }}
                onNext={(nextPage) => {
                  stopPolling();

                  // If we're already on the last page then get the last page of
                  // results w/out the cursor. This allows the user to get to
                  // results that may have drifted due paginating over data which
                  // is ingested in real-time.
                  if (nextPage === numPages) {
                    history.replace({
                      ...location,
                      search: mergeQueryParams(location.search, {
                        fieldsAfter: undefined,
                        fieldsBefore: undefined,
                        fieldsPage: nextPage,
                        fieldsPageDir: 'last',
                      }),
                    });
                  } else {
                    history.replace({
                      ...location,
                      search: mergeQueryParams(location.search, {
                        fieldsBefore: undefined,
                        fieldsAfter:
                          data.graph?.variant?.fieldInsightsList.pageInfo
                            .endCursor ?? undefined,
                        fieldsPage: nextPage,
                        fieldsPageDir: 'first',
                      }),
                    });
                  }

                  trackCustomEvent({
                    category: 'Insights L3 Fields List',
                    action: 'selected_next_page',
                  });
                }}
                onLast={(nextPage) => {
                  history.replace({
                    ...location,
                    search: mergeQueryParams(location.search, {
                      fieldsAfter: undefined,
                      fieldsBefore: undefined,
                      fieldsPage: nextPage,
                      fieldsPageDir: 'last',
                    }),
                  });

                  trackCustomEvent({
                    category: 'Insights L3 Fields List',
                    action: 'selected_last_page',
                  });
                }}
              />
            ) : null}
          </>
        )}
      </>
    </div>
  );
};
